"use client";

import { useEffect, useMemo, useState } from "react";
import { translateText, translateHtmlText } from "@/lib/i18n-dict";

export type ProductItem = {
  id?: string;
  category: string;
  zhName: string;
  enName?: string;
  standard?: string;
  material?: string;
  finish?: string;
  description?: string;
  image?: string;
  model?: string;
  unit?: string;
  availableQty?: number;
  drawingFiles?: ProductDrawingFile[];
};

type ProductDrawingFile = {
  id: string;
  productKey: string;
  title: string;
  url: string;
  fileType?: string;
};

type ViewLayout = "grid" | "list" | "compact" | "category";

type CartItem = ProductItem & {
  cartId: string;
  quantity: number;
};

type CustomerProfile = {
  company: string;
  contact: string;
  email: string;
  phone: string;
};

const CART_STORAGE_KEY = "rt-product-cart-v1";
const RFQ_PREFILL_KEY = "rt-cart-rfq-prefill";
const CUSTOMER_STORAGE_KEY = "rt-customer-profile-v1";

// 产品字段显示控制
export type ProductFieldVisibility = {
  image?: boolean;
  category?: boolean;
  standard?: boolean;
  material?: boolean;
  finish?: boolean;
  stock?: boolean;
  model?: boolean;
  cartButton?: boolean;
};

// 各视图默认每页数量
const DEFAULT_PAGE_SIZE: Record<Exclude<ViewLayout, "category">, number> = {
  grid: 64,      // 8x8
  list: 20,
  compact: 20,
};

// 默认全部显示
const DEFAULT_FIELD_VISIBILITY: Required<ProductFieldVisibility> = {
  image: true,
  category: true,
  standard: true,
  material: true,
  finish: true,
  stock: true,
  model: true,
  cartButton: true,
};

export function ProductCatalog({
  products,
  categories,
  defaultLayout = "grid",
  pageSizes,
  gridCols = 3,
  fieldVisibility,
  lang = "zh" as "zh" | "en",
}: {
  products: ProductItem[];
  categories: string[];
  defaultLayout?: ViewLayout;
  pageSizes?: Partial<Record<Exclude<ViewLayout, "category">, number>>;
  gridCols?: number;
  fieldVisibility?: ProductFieldVisibility;
  lang?: "zh" | "en";
}) {
  // 合并默认每页数量与传入配置（category 视图不做分页，每页数量用于兼容旧属性）
  const pageSizeConfig: Record<Exclude<ViewLayout, "category">, number> = {
    grid: pageSizes?.grid ?? DEFAULT_PAGE_SIZE.grid,
    list: pageSizes?.list ?? DEFAULT_PAGE_SIZE.list,
    compact: pageSizes?.compact ?? DEFAULT_PAGE_SIZE.compact,
  };

  // 合并字段显示配置
  const fields: Required<ProductFieldVisibility> = {
    image: fieldVisibility?.image ?? DEFAULT_FIELD_VISIBILITY.image,
    category: fieldVisibility?.category ?? DEFAULT_FIELD_VISIBILITY.category,
    standard: fieldVisibility?.standard ?? DEFAULT_FIELD_VISIBILITY.standard,
    material: fieldVisibility?.material ?? DEFAULT_FIELD_VISIBILITY.material,
    finish: fieldVisibility?.finish ?? DEFAULT_FIELD_VISIBILITY.finish,
    stock: fieldVisibility?.stock ?? DEFAULT_FIELD_VISIBILITY.stock,
    model: fieldVisibility?.model ?? DEFAULT_FIELD_VISIBILITY.model,
    cartButton: fieldVisibility?.cartButton ?? DEFAULT_FIELD_VISIBILITY.cartButton,
  };

  const [layout, setLayout] = useState<ViewLayout>(defaultLayout);
  const allZhCategory = lang === "zh" ? "全部" : "全部";
  const allLabel = translateText("全部", lang);
  const [activeCategory, setActiveCategory] = useState<string>(allZhCategory);
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartNotice, setCartNotice] = useState("");
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [customerForm, setCustomerForm] = useState<CustomerProfile>({ company: "", contact: "", email: "", phone: "" });
  const [customerError, setCustomerError] = useState("");
  const [pendingProduct, setPendingProduct] = useState<ProductItem | null>(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as CartItem[];
      if (Array.isArray(parsed)) {
        setCartItems(parsed.filter((item) => item.cartId && item.quantity > 0));
      }
    } catch {
      setCartItems([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOMER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as CustomerProfile;
      if (isCustomerComplete(parsed)) {
        setCustomerForm(parsed);
      }
    } catch {
      setCustomer(null);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // localStorage may be unavailable in private or restricted contexts.
    }
  }, [cartItems]);

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    return products.filter((p) => {
      const rawCat = lang === "en" ? translateText(activeCategory, "zh") : activeCategory;
      if (activeCategory !== allZhCategory && rawCat !== allZhCategory && p.category !== rawCat) return false;
      if (!k) return true;
      const haystack = [p.zhName, p.enName, p.model, p.material, p.finish, p.standard, p.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(k);
    });
  }, [products, activeCategory, keyword, allZhCategory, lang]);

  // 按分类分组（用于 category 视图；最多每个分类显示 8 条）
  const groupedByCategory = useMemo(() => {
    if (layout !== "category") return [] as Array<[string, ProductItem[]]>;
    const map = new Map<string, ProductItem[]>();
    for (const p of filtered) {
      const key = p.category || translateText("其他", "zh");
      const list = map.get(key) ?? [];
      if (list.length < 8) list.push(p);
      if (list.length === 1) map.set(key, list);
    }
    // 按现有分类顺序返回
    const ordered: Array<[string, ProductItem[]]> = [];
    for (const cat of categories) {
      if (map.has(cat)) ordered.push([cat, map.get(cat)!]);
    }
    for (const [cat, list] of map) {
      if (!categories.includes(cat)) ordered.push([cat, list]);
    }
    return ordered;
  }, [filtered, layout, categories]);

  // 当前视图每页数量
  const pageSize =
    layout === "category"
      ? Math.max(1, filtered.length)
      : pageSizeConfig[layout];
  const totalPages =
    layout === "category" ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleProducts =
    layout === "category" ? filtered : filtered.slice(startIndex, startIndex + pageSize);

  function changeLayout(next: ViewLayout) {
    setLayout(next);
    setPage(1);
  }

  // 网格列数：限制 2~8，默认 3
  const safeGridCols = Number.isFinite(gridCols) && gridCols >= 2 && gridCols <= 8 ? Math.round(gridCols) : 3;
  const gridStyle = {
    style: { gridTemplateColumns: `repeat(${safeGridCols}, minmax(0, 1fr))` } as React.CSSProperties,
  };

  function changeCategory(next: string) {
    setActiveCategory(next);
    setPage(1);
  }

  function addToCart(product: ProductItem) {
    if (!isProductInStock(product)) return;
    if (!customer) {
      setPendingProduct(product);
      setCustomerDialogOpen(true);
      setCustomerError("");
      return;
    }
    addProductToCart(product);
  }

  function addProductToCart(product: ProductItem) {
    const cartId = getProductCartId(product);
    setCartItems((current) => {
      const existing = current.find((item) => item.cartId === cartId);
      const maxQty = Math.max(1, Math.floor(product.availableQty ?? 1));
      if (existing) {
        return current.map((item) =>
          item.cartId === cartId
            ? { ...item, quantity: Math.min(maxQty, item.quantity + 1), availableQty: product.availableQty }
            : item,
        );
      }
      return [...current, { ...product, cartId, quantity: 1 }];
    });
    setCartOpen(true);
    setCartNotice(lang === "en" ? "Added to RFQ cart." : "已加入询价车。");
  }

  function saveCustomerProfile() {
    if (!customerForm.company.trim() || !customerForm.contact.trim() || (!customerForm.email.trim() && !customerForm.phone.trim())) {
      setCustomerError(lang === "en" ? "Company, contact, and email or phone are required." : "请填写公司、联系人，以及邮箱或电话。");
      return;
    }
    const nextCustomer = {
      company: customerForm.company.trim(),
      contact: customerForm.contact.trim(),
      email: customerForm.email.trim(),
      phone: customerForm.phone.trim(),
    };
    setCustomer(nextCustomer);
    setCustomerForm(nextCustomer);
    try {
      window.localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(nextCustomer));
    } catch {
      // Ignore localStorage failures; the current session still has the data.
    }
    setCustomerDialogOpen(false);
    setCustomerError("");
    if (pendingProduct) {
      addProductToCart(pendingProduct);
      setPendingProduct(null);
    }
  }

  function updateCartQuantity(cartId: string, nextQuantity: number) {
    setCartItems((current) =>
      current
        .map((item) => {
          if (item.cartId !== cartId) return item;
          const maxQty = Math.max(1, Math.floor(item.availableQty ?? nextQuantity));
          return { ...item, quantity: Math.min(maxQty, Math.max(1, Math.floor(nextQuantity || 1))) };
        })
        .filter((item) => item.quantity > 0),
    );
  }

  function removeCartItem(cartId: string) {
    setCartItems((current) => current.filter((item) => item.cartId !== cartId));
  }

  function clearCart() {
    setCartItems([]);
    setCartNotice(lang === "en" ? "Cart cleared." : "询价车已清空。");
  }

  function goToRfq() {
    const summary = buildCartSummary(cartItems, lang);
    try {
      window.localStorage.setItem(
        RFQ_PREFILL_KEY,
        JSON.stringify({
          summary,
          items: cartItems,
          customer,
          totalQuantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
          createdAt: new Date().toISOString(),
        }),
      );
    } catch {
      // Continue navigation even if localStorage fails.
    }
    window.location.href = lang === "en" ? "/en/rfq?cart=1" : "/rfq?cart=1";
  }

  const allCategories = [
    allZhCategory,
    ...categories.filter((category) => category && category !== allZhCategory),
  ];

  const placeholder = translateText("搜索产品名称、型号、材料、标准…", lang);
  const searchAria = translateText("搜索产品", lang);
  const viewAria = translateText("视图切换", lang);
  const titleGrid = translateText("网格视图", lang);
  const titleList = translateText("列表视图", lang);
  const titleCompact = translateText("紧凑视图", lang);
  const titleCategory = translateText("按分类分组视图", lang);
  const gridText = translateText("网格", lang);
  const listText = translateText("列表", lang);
  const compactText = translateText("紧凑", lang);
  const categoryText = translateText("按分类", lang);
  const emptyText = translateText("没有符合条件的产品，请调整搜索或过滤条件。", lang);
  const firstBtn = translateText("首页", lang);
  const prevBtn = translateText("上一页", lang);
  const nextBtn = translateText("下一页", lang);
  const lastBtn = translateText("末页", lang);
  const pieces = lang === "zh" ? "件产品" : "items";
  const metaPrefix = lang === "zh" ? "共" : "Total";
  const metaPer = lang === "zh" ? "每页" : "Per page";
  const metaPage = lang === "zh" ? "第" : "Page";
  const metaOf = lang === "zh" ? "/" : "/";
  const metaSuf1 = lang === "zh" ? "页" : "";
  const metaSearch = lang === "zh" ? `· 搜索"${keyword}"` : ` · Search "${keyword}"`;
  return (
    <div className="rt-catalog" data-rt-lang={lang}>
      <div className="rt-catalog-toolbar">
        <div className="rt-catalog-search">
          <input
            type="search"
            placeholder={placeholder}
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage(1);
            }}
            aria-label={searchAria}
          />
        </div>
        <div className="rt-catalog-views" role="group" aria-label={viewAria}>
          <button
            type="button"
            className={layout === "grid" ? "active" : ""}
            onClick={() => changeLayout("grid")}
            title={titleGrid}
          >
            {gridText}
          </button>
          <button
            type="button"
            className={layout === "list" ? "active" : ""}
            onClick={() => changeLayout("list")}
            title={titleList}
          >
            {listText}
          </button>
          <button
            type="button"
            className={layout === "compact" ? "active" : ""}
            onClick={() => changeLayout("compact")}
            title={titleCompact}
          >
            {compactText}
          </button>
          <button
            type="button"
            className={(layout as ViewLayout) === "category" ? "active" : ""}
            onClick={() => changeLayout("category")}
            title={titleCategory}
          >
            {categoryText}
          </button>
        </div>
      </div>

      <div className="rt-catalog-categories">
        {allCategories.map((category, index) => (
          <button
            key={`${category}-${index}`}
            type="button"
            className={activeCategory === category ? "active" : ""}
            onClick={() => changeCategory(category)}
          >
            {category === allZhCategory ? allLabel : translateText(category, lang)}
          </button>
        ))}
      </div>

      <div className="rt-catalog-meta">
        {metaPrefix} {filtered.length} {pieces} · {metaPer} {pageSize} · {metaPage} {currentPage}{metaOf}{totalPages} {metaSuf1}
        {keyword ? metaSearch : ""}
        {activeCategory !== allZhCategory
          ? lang === "zh"
            ? ` · ${activeCategory}`
            : ` · ${translateText(activeCategory, lang)}`
          : ""}
      </div>

      {layout === "category" ? (
        groupedByCategory.length === 0 ? (
          <div className="rt-dynamic-empty">{emptyText}</div>
        ) : (
          <div className="rt-product-category-grouped">
            {groupedByCategory.map(([cat, list], groupIndex) => (
              <section key={`${cat}-${groupIndex}`} className="rt-product-category-group">
                <h3>{translateText(cat, lang)}</h3>
                <div className="rt-product-dynamic-grid" {...gridStyle}>
                  {list.map((product, idx) => (
                  <ProductCard
                      key={getProductRenderKey(product, `${cat}-${idx}`)}
                      product={product}
                      layout="grid"
                      fields={fields}
                      lang={lang}
                      cartQuantity={cartItems.find((item) => item.cartId === getProductCartId(product))?.quantity ?? 0}
                      customerRegistered={Boolean(customer)}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )
      ) : (
        <>
          {visibleProducts.length === 0 ? (
            <div className="rt-dynamic-empty">{emptyText}</div>
          ) : (
            <div
              className={`rt-product-dynamic-grid ${layout}`}
              {...(layout === "grid" ? gridStyle : undefined)}
            >
              {visibleProducts.map((product, index) => (
                <ProductCard
                  key={getProductRenderKey(product, `${startIndex + index}`)}
                  product={product}
                  layout={layout}
                  fields={fields}
                  lang={lang}
                  cartQuantity={cartItems.find((item) => item.cartId === getProductCartId(product))?.quantity ?? 0}
                  customerRegistered={Boolean(customer)}
                  onAddToCart={addToCart}
                />
              ))}
            </div>
          )}
        </>
      )}

      {layout !== "category" && totalPages > 1 && (
        <div className="rt-catalog-pagination">
          <button type="button" onClick={() => setPage(1)} disabled={currentPage <= 1} title={firstBtn}>
            {firstBtn}
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            title={prevBtn}
          >
            {prevBtn}
          </button>
          {renderPageNumbers(currentPage, totalPages, setPage)}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            title={nextBtn}
          >
            {nextBtn}
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages)}
            disabled={currentPage >= totalPages}
            title={lastBtn}
          >
            {lastBtn}
          </button>
        </div>
      )}

      {fields.cartButton && (
        <CartPanel
          items={cartItems}
          open={cartOpen}
          lang={lang}
          notice={cartNotice}
          onOpen={() => setCartOpen(true)}
          onClose={() => setCartOpen(false)}
          onQuantityChange={updateCartQuantity}
          onRemove={removeCartItem}
          onClear={clearCart}
          onGoToRfq={goToRfq}
          onEditCustomer={() => {
            setCustomerDialogOpen(true);
            setCustomerError("");
          }}
        />
      )}
      <CustomerProfileDialog
        open={customerDialogOpen}
        lang={lang}
        value={customerForm}
        error={customerError}
        onChange={setCustomerForm}
        onCancel={() => {
          setCustomerDialogOpen(false);
          setPendingProduct(null);
        }}
        onSave={saveCustomerProfile}
      />
    </div>
  );
}

function renderPageNumbers(current: number, total: number, setPage: (p: number) => void) {
  const buttons: React.ReactNode[] = [];
  const maxVisible = 5;
  let start = Math.max(1, current - Math.floor(maxVisible / 2));
  const end = Math.min(total, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);

  if (start > 1) {
    buttons.push(
      <span key="ellipsis-start" className="rt-page-ellipsis">…</span>,
    );
  }

  for (let i = start; i <= end; i++) {
    buttons.push(
      <button
        key={i}
        type="button"
        className={i === current ? "active" : ""}
        onClick={() => setPage(i)}
      >
        {i}
      </button>,
    );
  }

  if (end < total) {
    buttons.push(
      <span key="ellipsis-end" className="rt-page-ellipsis">…</span>,
    );
  }

  return buttons;
}

function ProductCard({
  product,
  layout,
  fields,
  lang = "zh",
  cartQuantity = 0,
  customerRegistered = false,
  onAddToCart,
}: {
  product: ProductItem;
  layout: ViewLayout;
  fields: Required<ProductFieldVisibility>;
  lang?: "zh" | "en";
  cartQuantity?: number;
  customerRegistered?: boolean;
  onAddToCart?: (product: ProductItem) => void;
}) {
  // 列表视图不显示产品缩略图，紧凑/网格视图保留图位；同时受字段显示开关控制
  const showImage = layout !== "list" && fields.image;
  const hasImage = Boolean(product.image);
  // 型号/描述行：仅当开启且存在值时显示
  const rawDesc = product.model || product.description || product.standard || "";
  const modelText = fields.model ? rawDesc : "";
  const modelTextOut = lang === "en" && rawDesc ? translateText(modelText, "en") : modelText;

  const productName =
    lang === "en" ? (product.enName || translateText(product.zhName, "en")) : (product.zhName || product.enName || translateText("产品", lang));
  const productAlt = product.zhName || product.enName || productName;
  const categoryOut =
    fields.category
      ? lang === "en"
        ? translateText(product.category || translateText("产品", "zh"), "en")
        : (product.category || translateText("产品", lang))
      : "";
  const stdDt = translateText("标准", lang);
  const matDt = translateText("材料", lang);
  const finishDt = translateText("表面处理", lang);
  const stockDt = translateText("库存", lang);
  const noImageStr = translateText("暂无图片", lang);
  const canAddToCart = isProductInStock(product);
  const addCartText = customerRegistered
    ? lang === "en" ? "Add to RFQ cart" : "加入询价车"
    : lang === "en" ? "Register to add" : "登记后加入";
  const addedCartText = lang === "en" ? `In cart: ${cartQuantity}` : `已加 ${cartQuantity}`;
  const outStockText = lang === "en" ? "Out of stock" : "暂无库存";
  const drawings = Array.isArray(product.drawingFiles) ? product.drawingFiles.filter((item) => item.url) : [];
  const drawingLabel = lang === "en" ? "Drawings" : "产品图纸";

  const stdVal = product.standard
    ? lang === "en"
      ? translateText(product.standard, "en")
      : product.standard
    : "";
  const matVal = product.material
    ? lang === "en"
      ? translateText(product.material, "en")
      : product.material
    : "";
  const finishVal = product.finish
    ? lang === "en"
      ? translateText(product.finish, "en")
      : product.finish
    : "";

  return (
    <article className="rt-product-dynamic-card" data-rt-lang={lang}>
      {showImage && (
        hasImage ? (
          <img src={product.image} alt={productAlt} loading="lazy" />
        ) : (
          <div className="rt-product-no-image" aria-label={noImageStr}>
            <span>{noImageStr}</span>
          </div>
        )
      )}
      <div>
        {fields.category && <span>{categoryOut}</span>}
        <h3>{productName}</h3>
        {modelTextOut ? <p>{modelTextOut}</p> : null}
        <dl>
          {fields.standard && stdVal ? <div><dt>{stdDt}</dt><dd>{stdVal}</dd></div> : null}
          {fields.material && matVal ? <div><dt>{matDt}</dt><dd>{matVal}</dd></div> : null}
          {fields.finish && finishVal ? <div><dt>{finishDt}</dt><dd>{finishVal}</dd></div> : null}
          {fields.stock && typeof product.availableQty === "number" ? (
            <div><dt>{stockDt}</dt><dd>{product.availableQty.toLocaleString()}{product.unit || ""}</dd></div>
          ) : null}
        </dl>
        {drawings.length ? (
          <div className="rt-product-drawings" aria-label={drawingLabel}>
            <strong>{drawingLabel}</strong>
            <div>
              {drawings.slice(0, 3).map((drawing, index) => (
                <a key={`${drawing.id || drawing.url || drawing.title || "drawing"}-${index}`} href={drawing.url} target="_blank" rel="noreferrer">
                  {drawing.fileType ? `${drawing.fileType} · ` : ""}{drawing.title || drawingLabel}
                </a>
              ))}
            </div>
          </div>
        ) : null}
        {fields.cartButton && (
          <div className="rt-product-cart-actions">
            {canAddToCart ? (
              <button type="button" className="rt-add-cart-btn" onClick={() => onAddToCart?.(product)}>
                {cartQuantity > 0 ? addedCartText : addCartText}
              </button>
            ) : (
              <span className="rt-product-out-stock">{outStockText}</span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function CartPanel({
  items,
  open,
  lang,
  notice,
  onOpen,
  onClose,
  onQuantityChange,
  onRemove,
  onClear,
  onGoToRfq,
  onEditCustomer,
}: {
  items: CartItem[];
  open: boolean;
  lang: "zh" | "en";
  notice: string;
  onOpen: () => void;
  onClose: () => void;
  onQuantityChange: (cartId: string, quantity: number) => void;
  onRemove: (cartId: string) => void;
  onClear: () => void;
  onGoToRfq: () => void;
  onEditCustomer: () => void;
}) {
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const labels =
    lang === "en"
      ? {
          cart: "RFQ Cart",
          empty: "No products selected.",
          close: "Close",
          clear: "Clear",
          rfq: "Submit RFQ",
          customer: "Customer Info",
          stock: "Stock",
          qty: "Qty",
          remove: "Remove",
        }
      : {
          cart: "询价车",
          empty: "还没有选择产品。",
          close: "关闭",
          clear: "清空",
          rfq: "提交询价",
          customer: "客户信息",
          stock: "库存",
          qty: "数量",
          remove: "删除",
        };

  return (
    <>
      <button type="button" className="rt-cart-float" onClick={onOpen} aria-label={labels.cart}>
        <span>{labels.cart}</span>
        <b>{totalQuantity}</b>
      </button>
      {open && (
        <div className="rt-cart-backdrop" role="dialog" aria-modal="true" aria-label={labels.cart}>
          <aside className="rt-cart-panel">
            <div className="rt-cart-head">
              <div>
                <span>{labels.cart}</span>
                <strong>{totalQuantity}</strong>
              </div>
              <button type="button" onClick={onClose} aria-label={labels.close}>×</button>
            </div>
            {notice ? <p className="rt-cart-notice">{notice}</p> : null}
            {items.length === 0 ? (
              <div className="rt-cart-empty">{labels.empty}</div>
            ) : (
              <div className="rt-cart-list">
                {items.map((item) => {
                  const name = lang === "en" ? item.enName || translateText(item.zhName, "en") : item.zhName || item.enName || "";
                  return (
                    <div className="rt-cart-item" key={item.cartId}>
                      {item.image ? <img src={item.image} alt={name} /> : <div className="rt-cart-thumb" />}
                      <div className="rt-cart-item-body">
                        <strong>{name}</strong>
                        <span>{[item.standard, item.material, item.finish].filter(Boolean).join(" / ")}</span>
                        {item.drawingFiles?.length ? (
                          <small>
                            {lang === "en" ? "Drawings" : "图纸"}: {item.drawingFiles.map((drawing) => drawing.title).filter(Boolean).join("、")}
                          </small>
                        ) : null}
                        <small>{labels.stock}: {(item.availableQty ?? 0).toLocaleString()}{item.unit || ""}</small>
                        <div className="rt-cart-qty">
                          <label>{labels.qty}</label>
                          <button type="button" onClick={() => onQuantityChange(item.cartId, item.quantity - 1)}>-</button>
                          <input
                            value={item.quantity}
                            inputMode="numeric"
                            onChange={(event) => onQuantityChange(item.cartId, Number(event.target.value))}
                          />
                          <button type="button" onClick={() => onQuantityChange(item.cartId, item.quantity + 1)}>+</button>
                          <button type="button" className="rt-cart-remove" onClick={() => onRemove(item.cartId)}>
                            {labels.remove}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="rt-cart-footer">
              <button type="button" onClick={onEditCustomer}>{labels.customer}</button>
              <button type="button" onClick={onClear} disabled={items.length === 0}>{labels.clear}</button>
              <button type="button" className="primary" onClick={onGoToRfq} disabled={items.length === 0}>{labels.rfq}</button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function CustomerProfileDialog({
  open,
  lang,
  value,
  error,
  onChange,
  onCancel,
  onSave,
}: {
  open: boolean;
  lang: "zh" | "en";
  value: CustomerProfile;
  error: string;
  onChange: (value: CustomerProfile) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  if (!open) return null;
  const labels =
    lang === "en"
      ? {
          title: "Customer information",
          intro: "Please leave your contact information before adding products to the RFQ cart.",
          company: "Company *",
          contact: "Contact *",
          email: "Email",
          phone: "Phone",
          hint: "Email or phone: at least one is required.",
          cancel: "Cancel",
          save: "Save and add",
        }
      : {
          title: "客户信息登记",
          intro: "加入询价车前请先留下联系方式，方便我们确认产品需求。",
          company: "公司名称 *",
          contact: "联系人 *",
          email: "邮箱",
          phone: "电话",
          hint: "邮箱和电话至少填写一个。",
          cancel: "取消",
          save: "保存并加入",
        };

  return (
    <div className="rt-customer-backdrop" role="dialog" aria-modal="true" aria-label={labels.title}>
      <div className="rt-customer-dialog">
        <div className="rt-customer-head">
          <div>
            <h3>{labels.title}</h3>
            <p>{labels.intro}</p>
          </div>
          <button type="button" onClick={onCancel} aria-label={labels.cancel}>×</button>
        </div>
        <div className="rt-customer-grid">
          <label>
            <span>{labels.company}</span>
            <input value={value.company} onChange={(event) => onChange({ ...value, company: event.target.value })} />
          </label>
          <label>
            <span>{labels.contact}</span>
            <input value={value.contact} onChange={(event) => onChange({ ...value, contact: event.target.value })} />
          </label>
          <label>
            <span>{labels.email}</span>
            <input value={value.email} type="email" onChange={(event) => onChange({ ...value, email: event.target.value })} />
          </label>
          <label>
            <span>{labels.phone}</span>
            <input value={value.phone} onChange={(event) => onChange({ ...value, phone: event.target.value })} />
          </label>
        </div>
        <p className="rt-customer-hint">{error || labels.hint}</p>
        <div className="rt-customer-actions">
          <button type="button" onClick={onCancel}>{labels.cancel}</button>
          <button type="button" className="primary" onClick={onSave}>{labels.save}</button>
        </div>
      </div>
    </div>
  );
}

function getProductCartId(product: ProductItem) {
  return [product.id, product.zhName, product.enName, product.model, product.standard, product.material, product.finish]
    .filter(Boolean)
    .join("|");
}

function getProductRenderKey(product: ProductItem, fallback: string) {
  const stableParts = [
    product.id,
    product.zhName,
    product.enName,
    product.model,
    product.standard,
    product.material,
    product.finish,
    product.category,
    fallback,
  ];
  return stableParts
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("|");
}

function isProductInStock(product: ProductItem) {
  return typeof product.availableQty === "number" && product.availableQty > 0;
}

function isCustomerComplete(profile?: CustomerProfile | null) {
  return Boolean(profile?.company?.trim() && profile?.contact?.trim() && (profile?.email?.trim() || profile?.phone?.trim()));
}

function buildCartSummary(items: CartItem[], lang: "zh" | "en") {
  if (lang === "en") {
    return [
      "Selected products from RFQ cart:",
      ...items.map((item, index) => {
        const name = item.enName || translateText(item.zhName, "en");
        const drawings = item.drawingFiles?.length
          ? ` | Drawings: ${item.drawingFiles.map((drawing) => `${drawing.title} ${drawing.url}`).join("; ")}`
          : "";
        return `${index + 1}. ${name} | Qty: ${item.quantity}${item.unit || ""} | Standard: ${item.standard || "-"} | Material: ${item.material || "-"} | Finish: ${item.finish || "-"}${drawings}`;
      }),
    ].join("\n");
  }
  return [
    "询价车产品清单：",
    ...items.map((item, index) =>
      `${index + 1}. ${item.zhName || item.enName} | 数量：${item.quantity}${item.unit || ""} | 标准：${item.standard || "-"} | 材料：${item.material || "-"} | 表面处理：${item.finish || "-"}${item.drawingFiles?.length ? ` | 图纸：${item.drawingFiles.map((drawing) => `${drawing.title} ${drawing.url}`).join("；")}` : ""}`,
    ),
  ].join("\n");
}
